import { useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { Asset, Entry, EntrySkeletonType } from 'contentful';

import { createAdCarouselHook } from 'common/hooks/useAdCarousel';
import { logClickAd, PromotionSources } from 'config/analytics';
import { CreativeSlotTypes } from 'common/types/CreativeSlotTypes';
import { Ad } from 'common/api/contentful/models/AdEntry';
import { withHTTPS } from 'common/utils';
import { contentfulApi } from 'common/api/utils/contentful';
import { getContentfulPath } from 'utils/contentful';
import RemoteConfigWeb from 'config/RemoteConfigWeb';
import { FullAd } from 'common/api/store/models/HomePageLayout';
import { Hero, HeroAd } from '../contexts/contentful';

// Parsed banner type for component use
export interface CarouselBannerItem {
    id: string;
    imageUrl: string;
    mobileImageUrl?: string;
    imageAlt: string;
    hasLink: boolean;
    isFullWidthAd: boolean;
    // For fullWidthAd
    url?: string;
    title?: string;
    // For advertisement
    linkId?: string;
}

// Type guard to check if an entry is a FullAd (fullWidthAd content type)
const isFullAdEntry = (entry: any): boolean => {
    const fields = entry?.fields;
    return fields && ('desktopImage' in fields || 'mobileImage' in fields);
};

// Parse Contentful entry to CarouselBannerItem
const parseEntryToBanner = (entry: any): CarouselBannerItem => {
    if (isFullAdEntry(entry)) {
        const fields = entry.fields as FullAd;
        const desktopImage = (fields?.desktopImage as Asset)?.fields?.file?.url as
            | string
            | undefined;
        const mobileImage = (fields?.mobileImage as Asset)?.fields?.file?.url as string | undefined;
        const imageUrl = desktopImage || mobileImage || '';

        return {
            id: entry.sys.id,
            imageUrl: imageUrl ? withHTTPS(imageUrl) : '',
            mobileImageUrl: mobileImage ? withHTTPS(mobileImage) : undefined,
            imageAlt: (fields?.title as string) || '',
            hasLink: Boolean(fields?.url),
            isFullWidthAd: true,
            url: fields?.url as string,
            title: fields?.title as string,
        };
    }

    // Advertisement entry
    const fields = entry.fields as Ad;
    const file = fields.image?.fields?.file;
    const imageUrl = typeof file?.url === 'string' ? withHTTPS(file.url) : '';
    const imageTitle = fields.image?.fields?.title;

    return {
        id: entry.sys.id,
        imageUrl,
        imageAlt: (typeof imageTitle === 'string' ? imageTitle : fields.title) || '',
        hasLink: Boolean(fields.link?.sys?.id),
        isFullWidthAd: false,
        linkId: fields.link?.sys?.id,
        title: fields.title,
    };
};

type HeroMap = Record<string, Hero | HeroAd>;

// Base hook using common factory
const useAdCarouselBase = createAdCarouselHook<CarouselBannerItem>({
    getEntryId: async () => {
        await RemoteConfigWeb.initialize();
        return RemoteConfigWeb.getRemoteValue('ad_carousel_contentful_id_web').asString();
    },
    parseEntry: parseEntryToBanner,
});

interface UseAdCarouselResult {
    banners: CarouselBannerItem[];
    isLoading: boolean;
    handleBannerClick: (banner: CarouselBannerItem, index: number) => Promise<void>;
    getFilteredLayout: (layout: string[], heroes: HeroMap) => string[];
}

export const useAdCarousel = (): UseAdCarouselResult => {
    const history = useHistory();
    const { banners, isLoading } = useAdCarouselBase();

    const handleBannerClick = useCallback(
        async (banner: CarouselBannerItem, index: number) => {
            logClickAd({
                promotionName: banner.title || banner.id,
                index,
                source: PromotionSources.Contentful,
                creativeSlot: CreativeSlotTypes.P1Hero,
                carouselPosition: index + 1,
            });

            // Handle fullWidthAd - direct URL navigation (same tab like FullHeroAdRenderer)
            if (banner.isFullWidthAd) {
                if (!banner.url) {
                    return;
                }

                if (banner.url.startsWith('http')) {
                    window.location.href = banner.url;
                } else {
                    history.push(banner.url);
                }
                return;
            }

            // Handle advertisement - resolve link from Contentful
            if (!banner.linkId) {
                return;
            }

            try {
                const response = await contentfulApi.getEntry(banner.linkId);
                if (response.data) {
                    const { path, type } = getContentfulPath(
                        response.data as Entry<EntrySkeletonType>,
                        undefined
                    );
                    if (path) {
                        if (type === 'externalLink' || path.startsWith('http')) {
                            window.open(path, '_blank', 'noopener,noreferrer');
                        } else {
                            history.push(path);
                        }
                    }
                }
            } catch (error) {
                console.error('Failed to resolve banner link:', error);
            }
        },
        [history]
    );

    // Filter out the first hero from layout if carousel has banners (carousel replaces P1Hero)
    const getFilteredLayout = useCallback(
        (layout: string[], heroes: HeroMap): string[] => {
            if (banners.length === 0 || isLoading) {
                return layout;
            }
            // Find and remove the first hero from the layout
            const firstHeroIndex = layout.findIndex((id: string) => heroes[id]);
            if (firstHeroIndex === -1) {
                return layout;
            }
            return layout.filter((_: string, index: number) => index !== firstHeroIndex);
        },
        [banners.length, isLoading]
    );

    return { banners, isLoading, handleBannerClick, getFilteredLayout };
};
